/**
 * Shared types for the platform-split location source.
 *
 * Both `location-source.native.ts` and `location-source.web.ts` import from this
 * runtime-free module so neither has to import the other (which would be a
 * circular platform self-import).
 */

/** A geographic point. Matches the shape consumers already destructure. */
export interface Coords {
  latitude: number;
  longitude: number;
}

/**
 * Permission as the OS reports it. `undetermined` also covers "we asked and the
 * platform could not tell us" -- notably web browsers that cannot answer a
 * permissions query. It never means the user refused.
 */
export type LocationPermissionState = 'granted' | 'denied' | 'undetermined';

/** Copy for the services-disabled state, which means different things per platform. */
export interface ServicesDisabledCopy {
  title: string;
  body: string;
}

/**
 * How long to wait for a fresh fix before giving up.
 *
 * expo-location's `LocationOptions` has no `timeout` field, so this is enforced
 * by racing the position call against a timer. Without it, an emulator with no
 * location configured leaves `getCurrentPositionAsync` pending indefinitely.
 */
export const LOCATION_TIMEOUT_MS = 6000;

/** Cached fixes older than this are ignored in favour of a fresh lookup. */
export const LAST_KNOWN_MAX_AGE_MS = 5 * 60 * 1000;

/** A cached fix less accurate than this radius (metres) is not worth showing. */
export const LAST_KNOWN_REQUIRED_ACCURACY_M = 1000;

/**
 * Default fresh-fix retry policy: a single attempt, no delay.
 *
 * This is the native contract -- native has an instant `getLastKnownPositionAsync`
 * cache, so one fresh call is always enough. Web overrides both values (see
 * `location-source.web.ts`): a browser has no last-known cache and its
 * geolocation subsystem is cold on the first page load, so the one-shot call
 * routinely times out before the subsystem is warm. Retrying patiently there is
 * what keeps the first cold load from falsely reporting the location unavailable.
 *
 * The values are re-exported from the platform sources (not consumed from here
 * directly) so the engine reads them through the same platform-resolved
 * `@/utils/location-source` surface it reads the position functions from.
 */
export const FRESH_FIX_ATTEMPTS = 1;
export const FRESH_FIX_RETRY_DELAY_MS = 0;
